import React, { FC, useState } from 'react';
import { Icon } from '@/components/atomic/Icon';
import { Divider } from '@/components/Divider';
import { useLogSlideEvent } from '../hooks/useLogSlideEvent';
import { classnames } from '@/common/utils/classnames';
import { IStory } from '@/components/Story/Story.type';
import openHintBottomSheet from '../HintBottomSheet';
import { HintLoader } from './HintLoader';

interface IInfoSheetProps {
  items: IStory[];
  isLoading: boolean;
  isChangelog?: boolean;
}

export const HintThumbNails: FC<IInfoSheetProps> = ({
  items,
  isLoading,
  isChangelog = false,
}) => {
  const { logSlideEvent } = useLogSlideEvent(isChangelog);

  const shouldNeedsMoreButton = items?.length >= 5;

  const [shouldShowMore, setShouldShowMore] = useState(false);

  const returnShownStories = () => {
    if (shouldNeedsMoreButton && !shouldShowMore) {
      return items?.slice(0, 4);
    }
    return items;
  };

  return (
    <>
      <div
        className={classnames(
          'flex',
          shouldShowMore
            ? 'max-h-[280px] flex-col overflow-y-scroll scroll-nice'
            : 'flex-row',
          shouldNeedsMoreButton || isLoading
            ? 'justify-between gap-y-4'
            : 'mx-auto !justify-normal gap-x-4'
        )}
      >
        {isLoading ? (
          <HintLoader className="justify-between" />
        ) : (
          items?.length > 0 && (
            <>
              {returnShownStories().map((item, index) => (
                <div
                  key={item?.id}
                  className={classnames(
                    'flex cursor-pointer flex-col items-center',
                    shouldShowMore && '!flex-row'
                  )}
                  onClick={() => {
                    logSlideEvent(index + 1, 1);
                    openHintBottomSheet({
                      shouldOpenFullPage: true,
                      id: item?.id,
                      isChangelog,
                      highlightNumber: index + 1,
                    });
                  }}
                >
                  <div
                    className="flex h-14 w-14 items-center justify-center rounded-full"
                    style={{
                      background: item.isSeen
                        ? '#E1E5EB'
                        : 'linear-gradient(50deg, rgba(0,83,121,1) 5%, rgba(0,170,165,1) 100%)',
                    }}
                  >
                    <img
                      alt={item.title}
                      className="!h-[52px] !w-[52px] rounded-full border-[3px] border-white object-cover"
                      src={item.url}
                    />
                  </div>
                  <span
                    className={classnames(
                      'mt-2 text-xxs font-medium text-gray-filled-secondary-enabled',
                      shouldShowMore && 'mr-2'
                    )}
                  >
                    {item.title}
                  </span>
                </div>
              ))}
              {shouldNeedsMoreButton && (
                <div
                  className={classnames(
                    'flex cursor-pointer flex-col items-center',
                    shouldShowMore && '!flex-row'
                  )}
                  onClick={() => setShouldShowMore(!shouldShowMore)}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-filled-primary-enabled">
                    <div className="flex !h-[52px] !w-[52px] items-center justify-center rounded-full border-[3px] border-white object-cover">
                      <Icon
                        color="#747D8A"
                        icon={shouldShowMore ? 'chevron_up' : 'chevron_left'}
                        size={44}
                      />
                    </div>
                  </div>
                  <span
                    className={classnames(
                      'mt-2 text-xxs font-medium text-gray-filled-secondary-enabled',
                      shouldShowMore && 'mr-2'
                    )}
                  >
                    {shouldShowMore ? 'بازگشت' : 'بیشتر'}
                  </span>
                </div>
              )}
            </>
          )
        )}
      </div>
      {!isLoading && items.length !== 0 && <Divider />}
    </>
  );
};
